<script setup>
import { ref, onMounted, watch } from 'vue';

const emit = defineEmits(['update:inputDevice', 'update:outputDevice']);

const audioInputs = ref([]);
const audioOutputs = ref([]);

// 1. Initialise from localStorage immediately
const selectedInput = ref(localStorage.getItem('preferred-input') || '');
const selectedOutput = ref(localStorage.getItem('preferred-output') || '');

const getDevices = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const devices = await navigator.mediaDevices.enumerateDevices();
    
    audioInputs.value = devices.filter(device => device.kind === 'audioinput');
    audioOutputs.value = devices.filter(device => device.kind === 'audiooutput');

    // 2. Logic: If no saved device, or saved device no longer exists, default to index 0
    const inputExists = audioInputs.value.some(d => d.deviceId === selectedInput.value);
    if (!inputExists && audioInputs.value.length > 0) {
      selectedInput.value = audioInputs.value[0].deviceId;
    }

    const outputExists = audioOutputs.value.some(d => d.deviceId === selectedOutput.value);
    if (!outputExists && audioOutputs.value.length > 0) {
      selectedOutput.value = audioOutputs.value[0].deviceId;
    }

    stream.getTracks().forEach(track => track.stop());
    updateDevices();
  } catch (err) {
    console.error('Error accessing media devices:', err);
  }
};

const updateDevices = () => {
  // 3. Save to localStorage whenever they change
  localStorage.setItem('preferred-input', selectedInput.value);
  localStorage.setItem('preferred-output', selectedOutput.value);
  
  emit('update:inputDevice', selectedInput.value);
  emit('update:outputDevice', selectedOutput.value);
};

onMounted(() => {
  getDevices();
  navigator.mediaDevices.ondevicechange = getDevices;
});

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
});
</script>

<template>
  <!-- Input Device Field -->
  <div class="field">
    <label class="label">Microphone</label>
    <div class="control">
      <div class="select is-fullwidth">
        <select v-model="selectedInput" @change="updateDevices" :disabled="props.disabled">
          <option v-for="device in audioInputs" :key="device.deviceId" :value="device.deviceId">
            {{ device.label || 'Default Microphone' }}
          </option>
        </select>
      </div>
    </div>
  </div>

  <!-- Output Device Field -->
  <div class="field">
    <label class="label">Speaker / Output</label>
    <div class="control">
      <div class="select is-fullwidth">
        <select v-model="selectedOutput" @change="updateDevices" :disabled="props.disabled">
          <option v-for="device in audioOutputs" :key="device.deviceId" :value="device.deviceId">
            {{ device.label || 'Default Speaker' }}
          </option>
        </select>
      </div>
    </div>
    <p class="help">Note: Output selection is mainly supported in Chrome and Edge.</p>
  </div>
</template>